import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

// Static bet types
const betTypes = ['Total', 'Spread', 'Moneyline'];

// Static period types per sport
const periodTypesBySport = {
  NBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  WNBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half'],
};

// Static list of bookmakers in specified order
const staticBookmakers = [
  'Pinnacle',
  'Bookmaker',
  'Circa',
  'FanDuel',
  'DraftKings',
  'Bet365',
  'ESPN BET',
  'Caesars',
  'BetMGM',
  'BetRivers',
  'Bovada',
  'Fliff',
  'Hard Rock'
];

// Mapping between dropdown labels and betTypeId values (1 = Moneyline, 2 = Spread, 3 = Total)
const betTypeMapping = {
  Total: 3,
  Moneyline: 1,
  Spread: 2,
};

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const Odds = ({ bets, updatesPaused }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useRef(null);
  
  const [sport, setSport] = useState('MLB');
  const [betType, setBetType] = useState('Total');
  const [periodType, setPeriodType] = useState('Full Game');

  // Use static period types based on the selected sport
  const periodTypes = useMemo(() => {
    return periodTypesBySport[sport] || ['Full Game'];
  }, [sport]);

  // Reset periodType when sport changes if current periodType is not available
  useEffect(() => {
    if (!periodTypes.includes(periodType)) {
      setPeriodType(periodTypes[0] || 'Full Game');
    }
  }, [periodTypes, periodType]);

  // Group bets into a single table
  const tableData = useMemo(() => {
    // Ensure bets is an array
    if (!Array.isArray(bets)) {
      console.log('Bets data is not an array:', bets);
      return { rows: [], columns: [], tableHeight: 0 };
    }

    // Filter bets based on selected sport (but not betType or periodType yet)
    const filteredBets = bets.filter(bet => {
      if (!bet) return false;
      const matchesSport = bet.leagueName === sport;
      const matchesCategory = bet.betCategory === 'Game Line';
      return matchesSport && matchesCategory;
    });

    console.log('Filtered bets for Odds table:', filteredBets.length, 'items');

    // Collect bookmaker logos from the data
    const bookmakerLogos = {};
    filteredBets.forEach(bet => {
      if (!bookmakerLogos[bet.marketSourceName]) {
        bookmakerLogos[bet.marketSourceName] = bet.logourl || '';
      }
    });

    // Collect all unique events across all bet types and period types
    const eventGroups = {};
    filteredBets.forEach(bet => {
      const eventKey = bet.eventName;
      if (!eventGroups[eventKey]) {
        eventGroups[eventKey] = {
          event: bet.eventName,
          startTime: bet.eventStart,
          bets: [],
        };
      }
      eventGroups[eventKey].bets.push(bet);
    });

    // Create rows for the table
    const rowsWithStartTime = [];
    let rowId = 0;

    Object.entries(eventGroups).forEach(([eventKey, group]) => {
      const row = {
        id: rowId++,
        eventName: group.event,
        awayTeam: '',
        homeTeam: '',
        awayTeamLogo: '',
        homeTeamLogo: '',
        eventStart: group.startTime ? new Date(group.startTime) : new Date(),
        eventStartFormatted: {
          date: group.startTime
            ? new Date(group.startTime).toLocaleDateString('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
              })
            : '',
          time: group.startTime
            ? new Date(group.startTime).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { '0': '', '1': '', 'Over': '', 'Under': '' },
      };

      // Use teamName and teamLogo fields to set awayTeam, homeTeam, awayTeamLogo, and homeTeamLogo
      const referenceBets = group.bets.filter(bet => 
        (bet.betTypeId === 1 || bet.betTypeId === 2 ) // Use betTypeId for Moneyline (1) and Spread (2)
      );
      if (referenceBets.length > 0) {
        const awayBet = referenceBets.find(bet => bet.sideId === 0);
        const homeBet = referenceBets.find(bet => bet.sideId === 1);
        row.awayTeam = awayBet ? awayBet.teamName : 'Unknown Team A';
        row.homeTeam = homeBet ? homeBet.teamName : 'Unknown Team B';
        row.awayTeamLogo = awayBet && awayBet.teamLogo ? awayBet.teamLogo : '';
        row.homeTeamLogo = homeBet && homeBet.teamLogo ? homeBet.teamLogo : '';
      } else {
        const [parsedTeamA, parsedTeamB] = group.event.split(' @ ');
        const expectedAway = parsedTeamA ? parsedTeamA.trim().replace(/ -$/, '') : 'Unknown Team A';
        const expectedHome = parsedTeamB ? parsedTeamB.trim().replace(/ -$/, '') : 'Unknown Team B';
        row.awayTeam = expectedAway? expectedAway: 'Unknown Team A';
        row.homeTeam = expectedHome? expectedHome: 'Unknown Team B';
        row.awayTeamLogo = group.bets[0].awayLogo ? group.bets[0].awayLogo : '';
        row.homeTeamLogo = group.bets[0].homeLogo ? group.bets[0].homeLogo : '';
      }

      // Filter bets for the current betType and periodType to populate odds
      const oddsBets = group.bets.filter(bet => {
        const betTypeId = bet.betTypeId;
        const expectedBetTypeId = betTypeMapping[betType];
        const matchesBetType = betTypeId != null && betTypeId == expectedBetTypeId; // Use betTypeId for filtering
        const matchesPeriodType = bet.periodTypeName === periodType;
        return matchesBetType && matchesPeriodType;
      });

      // Collect outcomes based on bet type
      const outcomes = {};
      const outcomeDetails = {};
      if (betType === 'Moneyline' || betType === 'Spread') {
        outcomes['0'] = {};
        outcomes['1'] = {};
        outcomeDetails['0'] = {};
        outcomeDetails['1'] = {};
        oddsBets.forEach(bet => {
          const sideId = bet.sideId !== undefined ? bet.sideId.toString() : (bet.side === 'Home' ? '1' : '0');
          outcomes[sideId][bet.marketSourceName] = bet.price;
          outcomeDetails[sideId][`${bet.marketSourceName}Points`] = bet.points;
          outcomeDetails[sideId][`${bet.marketSourceName}Logo`] = bet.logourl || '';
          outcomeDetails[sideId][`${bet.marketSourceName}OutcomeName`] = bet.side || (sideId === '0' ? 'Away' : 'Home');
          outcomeDetails[sideId][`${bet.marketSourceName}LineDisplay`] = '';
          if (betType === 'Spread') {
            outcomeDetails[sideId][`${bet.marketSourceName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          }
        });
      } else {
        outcomes['Over'] = {};
        outcomes['Under'] = {};
        outcomeDetails['Over'] = {};
        outcomeDetails['Under'] = {};
        oddsBets.forEach(bet => {
          const outcome = bet.side === 'Over' ? 'Over' : 'Under';
          outcomes[outcome][bet.marketSourceName] = bet.price;
          outcomeDetails[outcome][`${bet.marketSourceName}Points`] = bet.points;
          outcomeDetails[outcome][`${bet.marketSourceName}Logo`] = bet.logourl || '';
          outcomeDetails[outcome][`${bet.marketSourceName}OutcomeName`] = bet.side || outcome;
          outcomeDetails[outcome][`${bet.marketSourceName}LineDisplay`] = `${outcome === 'Over' ? 'o' : 'u'}${bet.points || ''}`;
        });
      }

      // Determine the best odds for each outcome
      const bestOdds = {};
      let bestOddsKeys;
      if (betType === 'Moneyline' || betType === 'Spread') {
        bestOddsKeys = ['0', '1'];
      } else {
        bestOddsKeys = ['Over', 'Under'];
      }

      bestOddsKeys.forEach(outcome => {
        const entries = [];
        staticBookmakers.forEach(book => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          const points = outcomeDetails[outcome][`${book}Points`];
          if (price !== null) {
            entries.push({
              book,
              price,
              points: points !== undefined ? parseFloat(points) : null,
              logo: outcomeDetails[outcome][`${book}Logo`],
              outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
              lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
            });
          }
        });

        let bestEntry = null;
        if (entries.length > 0) {
          if (betType === 'Total') {
            entries.sort((a, b) => {
              if (a.points !== b.points) {
                return outcome === 'Over' ? a.points - b.points : b.points - a.points;
              }
              return b.price - a.price;
            });
            bestEntry = entries[0];
          } else {
            entries.sort((a, b) => b.price - a.price);
            bestEntry = entries[0];
          }
        }

        if (bestEntry) {
          bestOdds[outcome] = {
            price: bestEntry.price,
            points: bestEntry.points,
            logo: bestEntry.logo,
            outcomeName: bestEntry.outcomeName,
            lineDisplay: bestEntry.lineDisplay,
          };
          row.bestOddsBookmakers[outcome] = bestEntry.book;
        } else {
          bestOdds[outcome] = {
            price: null,
            points: null,
            logo: '',
            outcomeName: outcome === '0' ? 'Away' : 'Home',
            lineDisplay: '',
          };
          row.bestOddsBookmakers[outcome] = '';
        }
      });

      // Populate bookmaker odds for the row
      staticBookmakers.forEach(book => {
        const odds = {};
        bestOddsKeys.forEach(outcome => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          if (price !== null) {
            const formattedPrice = (betType === 'Moneyline' || betType === 'Total') && price > 0 ? `+${price}` : `${price}`;
            const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
            odds[outcome] = line ? `${line} ${formattedPrice}` : formattedPrice;
          } else {
            odds[outcome] = '';
          }
        });
        row.bookmakerOdds[book] = odds;
        row[book] = '';
      });

      // Format bestOdds for display
      bestOddsKeys.forEach(outcome => {
        const { price, lineDisplay } = bestOdds[outcome] || {};
        if (price !== null) {
          const formattedPrice = (betType === 'Moneyline' || betType === 'Total') && price > 0 ? `+${price}` : `${price}`;
          bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${formattedPrice}` : formattedPrice;
        } else {
          bestOdds[outcome].display = '';
        }
      });

      row.bestOdds = bestOdds;
      rowsWithStartTime.push(row);
    });

    // Sort rows by eventStart ascending
    rowsWithStartTime.sort((a, b) => a.eventStart - b.eventStart);

    // Calculate table height based on number of rows, with a minimum height
    const rowHeight = 70;
    const headerHeight = 56;
    const headerBorderHeight = 2;
    const minHeight = headerHeight + headerBorderHeight + (rowHeight * 2); // Minimum height for header + 2 rows
    const calculatedHeight = (rowsWithStartTime.length * rowHeight) + headerHeight + headerBorderHeight;
    const tableHeight = Math.max(minHeight, calculatedHeight);

    return { rows: rowsWithStartTime, columns: [], tableHeight };
  }, [bets, sport, betType, periodType]);

  // Collect bookmaker logos from the data in a separate memo
  const bookmakerLogos = useMemo(() => {
    const logos = {};
    (bets || []).forEach(bet => {
      if (bet && !logos[bet.marketSourceName]) {
        logos[bet.marketSourceName] = bet.logourl || '';
      }
    });
    return logos;
  }, [bets]);

  // Get saved column order for current context
  const getSavedColumnOrder = () => {
    try {
      const key = `oddsColumnOrder_${sport}_${betType}_${periodType}_${isMobile ? 'mobile' : 'desktop'}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column order:', error);
    }
    return null;
  };

  // Save column order for current context
  const saveColumnOrder = (order) => {
    try {
      const key = `oddsColumnOrder_${sport}_${betType}_${periodType}_${isMobile ? 'mobile' : 'desktop'}`;
      localStorage.setItem(key, JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column order:', error);
    }
  };

  // Define columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'eventName',
        headerName: '',
        minWidth: isMobile ? 35 : 230,
        width: isMobile ? 35 : undefined,
        flex: isMobile ? 1 : 1,
        sortable: false,
        disableReorder: true, // Prevent this column from being reordered
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
              {params.row.awayTeamLogo && (
                <img
                  src={params.row.awayTeamLogo}
                  alt={`${params.row.awayTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }}
                />
              )}
              {!isMobile && <Typography sx={{ fontSize: '14px' }}>{params.row.awayTeam}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
              {params.row.homeTeamLogo && (
                <img
                  src={params.row.homeTeamLogo}
                  alt={`${params.row.homeTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }}
                />
              )}
              {!isMobile && <Typography sx={{ fontSize: '14px' }}>{params.row.homeTeam}</Typography>}
            </Box>
          </Box>
        ),
      },
      {
        field: 'eventStartFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true, // Prevent this column from being reordered
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds',
        headerName: 'Best Odds',
        headerAlign: 'right',
        minWidth: isMobile ? 110 : 135,
        flex: 1,
        sortable: false,
        disableReorder: true, // Prevent this column from being reordered
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          const outcomes = betType === 'Moneyline' || betType === 'Spread' ? ['0', '1'] : ['Over', 'Under'];
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
              {outcomes.map(outcome => {
                const { display, logo, outcomeName } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '8px',
                        flex: 1,
                      }}
                    >
                      {display}
                    </Typography>
                    {logo && (
                      <img
                        src={logo}
                        alt={`${outcomeName} Logo`}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    // Only show a subset of bookmakers on mobile
    const visibleBookmakers = staticBookmakers;
    
    // Create bookmaker columns
    const bookmakerColumns = {};
    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book,
        headerName: '',
        headerAlign: 'right',
        minWidth: isMobile ? 90 : 100,
        flex: 1,
        sortable: false,
        disableReorder: false, // Allow these columns to be reordered
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '12px' }}>
            {bookmakerLogos[book] ? (
              <img
                src={bookmakerLogos[book]}
                alt={book}
                style={{ width: isMobile ? '50px' : '70px', height: 'auto', objectFit: 'contain' }}
              />
            ) : (
              <Box sx={{ width: isMobile ? '50px' : '70px', height: 'auto' }} />
            )}
          </Box>
        ),
        renderCell: (params) => {
          const row = params.row;
          const bookOdds = row.bookmakerOdds && row.bookmakerOdds[params.field];
          const bestBookmaker = row.bestOddsBookmakers;
          if (bookOdds) {
            const outcomes = betType === 'Moneyline' || betType === 'Spread' ? ['0', '1'] : ['Over', 'Under'];
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                {outcomes.map(outcome => (
                  <Box
                    key={outcome}
                    sx={{
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      border: params.field === bestBookmaker[outcome] ? '2px solid #00cc00' : 'none',
                      borderRadius: '0',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '12px',
                        flex: 1,
                      }}
                    >
                      {bookOdds[outcome] || ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          }
          return <Box sx={{ textAlign: 'right', pr: '12px' }}><Typography sx={{ fontSize: '14px' }}></Typography></Box>;
        },
      };
    });

    // Get saved order or use default
    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    
    if (savedOrder && savedOrder.bookmakers) {
      // Add bookmaker columns in saved order
      savedOrder.bookmakers.forEach(book => {
        if (bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
      
      // Add any new bookmaker columns not in saved order
      visibleBookmakers.forEach(book => {
        if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
    } else {
      // No saved order, use default
      visibleBookmakers.forEach(book => {
        finalColumns.push(bookmakerColumns[book]);
      });
    }

    return finalColumns;
  }, [sport, betType, periodType, isMobile, bookmakerLogos]);

  // Handle column reordering
  const handleColumnOrderChange = (params) => {
    // DataGridPro might pass the new order in different ways
    if (apiRef.current) {
      const state = apiRef.current.state;
      const orderedFields = state.columns.orderedFields || state.columns.all;
      
      if (orderedFields) {
        // Extract bookmaker columns
        const fixedColumns = ['eventName', 'eventStartFormatted', 'bestOdds'];
        const bookmakers = orderedFields.filter(field => !fixedColumns.includes(field));
        
        // Save the new order
        saveColumnOrder({
          fixed: fixedColumns,
          bookmakers: bookmakers
        });
      }
    }
  };

  // Restore column order when grid is ready
  useEffect(() => {
    if (apiRef.current && columns.length > 0) {
      const savedOrder = getSavedColumnOrder();
      if (savedOrder && savedOrder.bookmakers) {
        const fullOrder = [...savedOrder.fixed, ...savedOrder.bookmakers];
        
        // Use setTimeout to ensure grid is fully initialized
        setTimeout(() => {
          try {
            if (apiRef.current && apiRef.current.setColumnOrder) {
              apiRef.current.setColumnOrder(fullOrder);
            }
          } catch (error) {
            console.error('Error restoring column order:', error);
          }
        }, 100);
      }
    }
  }, [sport, betType, periodType, isMobile, columns]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: 'background.default' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px' }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 1 : 2} 
              alignItems={isMobile ? "stretch" : "center"}
            >
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="sport-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                <Select
                  labelId="sport-filter-label"
                  label="League"
                  notched
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="MLB">MLB</MenuItem>
                  <MenuItem value="NBA">NBA</MenuItem>          
                  <MenuItem value="WNBA">WNBA</MenuItem>          
                  <MenuItem value="NFL">NFL</MenuItem>
                  <MenuItem value="NHL">NHL</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                <Select
                  labelId="bet-type-filter-label"
                  label="Bet Type"
                  notched
                  value={betType}
                  onChange={(e) => setBetType(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {betTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="period-type-filter-label" shrink sx={{ color: '#ffffff' }}>Period Type</InputLabel>
                <Select
                  labelId="period-type-filter-label"
                  label="Period Type"
                  notched
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {periodTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
          <Box className="table-section">
            <Box sx={{ width: '100%' }}>
              {tableData.rows.length === 0 ? (
                <Box className="empty-state" sx={{ padding: '20px', textAlign: 'center' }}>
                  <Typography variant="body1">
                    No odds data available for the selected filters.
                  </Typography>
                </Box>
              ) : (
                <div style={{ height: tableData.tableHeight, width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={tableData.rows}
                    columns={columns}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    disableColumnSort
                    disableColumnMenu
                    hideFooter
                    rowHeight={70}
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e1e1e', borderBottom: '2px solid #444' },
                      '& .MuiDataGrid-columnHeader': {
                        userSelect: 'none',
                        cursor: 'move',
                      },
                      '& .MuiDataGrid-columnHeader--alignRight': {
                        justifyContent: 'flex-end !important',
                      },
                      '& .MuiDataGrid-cell': {
                        color: '#ffffff !important',
                        userSelect: 'none',
                        padding: 0,
                        borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                      },
                      '& .MuiDataGrid-cell:last-child': {
                        borderRight: 'none', // Remove border from last column
                      },
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                      '& .MuiDataGrid-columnSeparator': { 
                        visibility: 'visible',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: isMobile ? 'auto' : 'hidden !important',
                      },
                      '& .MuiDataGrid-root': {
                        overflowY: isMobile ? 'auto' : 'hidden !important',
                        margin: 0,
                      },
                      '& .MuiDataGrid-main': {
                        padding: 0,
                      },
                      '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                      '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
                      '& .MuiSelect-icon': { color: '#ffffff !important' },
                      '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                      '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#ffffff !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      },
                      '& .MuiDataGrid-columnHeaderDraggableContainer': {
                        cursor: 'move',
                      },
                    }}
                    rowBuffer={5}
                    rowThreshold={2}
                  />
                </div>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Odds;